interface Point {
  checkedAt: string;
  responseTimeMs: number | null;
  httpStatus: number | null;
}

interface Props {
  points: Point[];
  /** Hauteur du tracé en pixels ; la largeur s'adapte au conteneur. */
  height?: number;
}

/**
 * Courbe de latence sur sept jours, en SVG écrit à la main.
 *
 * Aucune bibliothèque de graphiques : sept jours de mesures toutes les cinq minutes font environ
 * 2 000 points, ce qu'un `polyline` avale sans effort. Importer une bibliothèque de plusieurs
 * centaines de kilooctets pour tracer une ligne serait disproportionné — et contraire à la CSP
 * stricte, qui interdit toute ressource externe.
 *
 * Le tracé utilise un `viewBox` en coordonnées normalisées et `preserveAspectRatio="none"` : le
 * SVG s'étire à la largeur disponible sans qu'on ait à mesurer le conteneur en JavaScript.
 */
export function Sparkline({ points, height = 80 }: Props) {
  const mesures = points.filter(
    (point): point is Point & { responseTimeMs: number } => point.responseTimeMs !== null,
  );

  if (mesures.length < 2) {
    return <p className="aide">Pas encore assez de mesures pour tracer une courbe.</p>;
  }

  const latences = mesures.map((point) => point.responseTimeMs);
  const max = Math.max(...latences);
  const min = Math.min(...latences);
  // Une amplitude nulle donnerait une division par zéro ; on force une échelle minimale.
  const amplitude = max - min || 1;

  const largeur = 1000;
  const chemin = mesures
    .map((point, index) => {
      const x = (index / (mesures.length - 1)) * largeur;
      const y = height - ((point.responseTimeMs - min) / amplitude) * (height - 8) - 4;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  // Les échecs sont marqués séparément : une latence basse sur une réponse 500 n'est pas une
  // bonne nouvelle, et la courbe seule le laisserait croire.
  const échecs = mesures
    .map((point, index) => ({ point, index }))
    .filter(({ point }) => point.httpStatus === null || point.httpStatus >= 400);

  const médiane = [...latences].sort((a, b) => a - b)[Math.floor(latences.length / 2)] ?? 0;

  return (
    <figure className="sparkline">
      <svg
        viewBox={`0 0 ${String(largeur)} ${String(height)}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Latence sur sept jours : médiane ${String(médiane)} millisecondes, maximum ${String(max)}`}
      >
        <polyline className="sparkline-trace" points={chemin} />
        {échecs.map(({ point, index }) => (
          <circle
            key={point.checkedAt}
            className="sparkline-echec"
            cx={((index / (mesures.length - 1)) * largeur).toFixed(1)}
            cy={(height - ((point.responseTimeMs - min) / amplitude) * (height - 8) - 4).toFixed(1)}
            r={6}
          />
        ))}
      </svg>
      <figcaption>
        {mesures.length} mesures sur 7 jours · médiane {médiane} ms · maximum {max} ms
        {échecs.length > 0 && ` · ${échecs.length} réponse(s) en erreur`}
      </figcaption>
    </figure>
  );
}
